import { instance } from '@/dashboard/api/instance';
import { Draft, DraftType } from '@/dashboard/types/draft';
import { ApiSuccessResponse } from '@/dashboard/api/types/apiTypes';

export const DraftsApi = {
  list: async ({ page, short_link }: DraftsListProps): Promise<DraftsListResponse> => {
    const result = await instance.get('/app/drafts', {
      params: { page, short_link },
    });
    return result.data;
  },
  get: async (id: number): Promise<DraftGetResponse> => {
    const result = await instance.get(`/app/drafts/${id}`);
    return result.data;
  },
  getByShortLink: async (shortLink: string): Promise<DraftGetResponse> => {
    const result = await instance.get(`/app/drafts/show_by_short_link`, {
      params: {
        short_link: shortLink,
      },
    });
    return result.data;
  },
  generateName: async (name: string): Promise<GenerateNameResponse> => {
    const result = await instance.post('/app/drafts/generate_name', {
      name,
    });
    return result.data;
  },
  create: async (data: DraftCreateProps): Promise<DraftCreateResponse> => {
    const result = await instance.post('/app/drafts', data);
    return result.data;
  },
  initiateUpload: async (data: InitiateUploadProps): Promise<InitiateUploadResponse> => {
    const result = await instance.post(`/app/drafts/initiate_upload`, data);
    return result.data;
  },
  getPreSignedUrl: async (params: GetPreSignedUrlProps): Promise<GetPreSignedUrlResponse> => {
    const result = await instance.get('/app/drafts/pre_signed_url', {
      params,
    });
    return result.data;
  },
  getDownloadUrl: async (params: GetDownloadUrlProps): Promise<GetDownloadUrlResponse> => {
    const result = await instance.get('/app/drafts/download_signed_url', {
      params,
    });
    return result.data;
  },
  completeUpload: async (data: CompleteUploadProps) => {
    const result = await instance.post(`/app/drafts/complete_upload`, data);
    return result.data;
  },
  bulkMove: async (data: BulkMoveProps) => {
    const result = await instance.post(`/app/drafts/bulk_move`, data);
    return result.data;
  },
  delete: async (ids: number | number[]) => {
    const idsArray = Array.isArray(ids) ? ids : [ids];
    const result = await instance.delete(`/app/drafts`, { data: { ids: idsArray } });
    return result.data;
  },
};

type DraftsListProps = {
  page: number;
  short_link: string;
};

type DraftsListResponse = {
  drafts: Draft[];
  project_id: number;
  short_link: string;
};

export type DraftGetResponse = ApiSuccessResponse & {
  draft: Draft | null;
  points: any[];
  followed: boolean;
};

type DraftCreateResponse = ApiSuccessResponse & {
  draft?: Draft;
  project_id?: number;
  project_name?: string;
  project_short_link?: string;
  workspace_id?: number;
  workspace_name?: string;
  workspace_short_link?: string;
  upload_limit?: boolean;
};

type GenerateNameResponse = {
  name: string | null | undefined;
};

type DraftCreateProps = {
  draft_type: DraftType;
  short_link: string;
  name: string;
  image_link?: string;
  file_link?: string;
  width?: number;
  height?: number;
  preview?: Blob;
};

type InitiateUploadProps = {
  filename: string;
  short_link: string;
  file_size: number;
};

type InitiateUploadResponse = {
  upload_id: string;
  salt: string;
};

type GetPreSignedUrlProps = {
  filename: string;
  part_number: number;
  upload_id: string;
  salt: string;
};

type GetPreSignedUrlResponse = {
  signed_url: string;
};

type GetDownloadUrlProps = {
  short_link: string;
  filename: string;
};

type GetDownloadUrlResponse = ApiSuccessResponse & {
  url: string | null;
};

type CompleteUploadProps = {
  filename: string;
  upload_id: string;
  salt: string;
  parts: UploadPart[];
};

type BulkMoveProps = {
  draft_ids: number[];
  project_id: number;
};

export type UploadPart = { part_number: number; etag: string };
